import React, { useEffect } from "react";
import {
  X, Loader2, MoreHorizontal, Crown, Eye, UserCog, Target, KeyRound, RotateCcw, Lock, Unlock,
  Mail, ShieldQuestion, Trash2, LayoutGrid, Users2, Building2, SlidersHorizontal, ScrollText,
  ChevronLeft, ChevronRight, Download,
} from "lucide-react";

export const ROLE_KEYS = ["super_admin", "client", "client_staff", "team_member"];
export const ROLE_LABEL = { super_admin: "Super Admin", client: "Kunden-Admin", client_staff: "Mitarbeiter", team_member: "Teammitglied" };
export const ROLE_CHIP = {
  super_admin: "bg-[#A3E635]/12 border-[#A3E635]/30 text-[#A3E635]",
  client: "bg-sky-500/12 border-sky-500/30 text-sky-300",
  client_staff: "bg-white/5 border-white/15 text-gray-300",
  team_member: "bg-violet-500/12 border-violet-500/30 text-violet-300",
};
export const ROLE_DESC = {
  super_admin: "Voller Zugriff auf das gesamte Control OS.",
  client: "Verwaltet die eigene Firma im Kunden-Cockpit.",
  client_staff: "Eingeschränkter Zugriff auf das Kunden-Cockpit.",
  team_member: "Internes KEWAG-Teammitglied (Betreuung).",
};
export const GLOBAL_ROLES = ["super_admin", "team_member"];

// 8-stufige interne Team-Rollen-Matrix (Farbcodierung)
export const TEAM_ROLE_CHIP = {
  super_admin: "bg-[#A3E635]/12 border-[#A3E635]/30 text-[#A3E635]",
  admin_ops: "bg-violet-500/12 border-violet-500/30 text-violet-300",
  projektleitung: "bg-sky-500/12 border-sky-500/30 text-sky-300",
  entwickler: "bg-cyan-500/12 border-cyan-500/30 text-cyan-300",
  support: "bg-amber-500/12 border-amber-500/30 text-amber-300",
  buchhaltung: "bg-emerald-500/12 border-emerald-500/30 text-emerald-300",
  content_seo: "bg-fuchsia-500/12 border-fuchsia-500/30 text-fuchsia-300",
  viewer: "bg-white/5 border-white/15 text-gray-300",
};
export const SCOPE_LABEL = { alle: "Alle Kunden", bestimmte: "Bestimmte Kunden", keine: "Keine Kunden" };

export const EVENT_LABEL = {
  login_erfolg: "Login erfolgreich", login_fehlgeschlagen: "Login fehlgeschlagen", login_gesperrt: "Login gesperrt",
  konto_gesperrt: "Konto gesperrt", konto_entsperrt: "Konto entsperrt", sperre_aufgehoben: "Konto entsperrt",
  rolle_geaendert: "Rolle geändert", team_rolle_geaendert: "Team-Rolle geändert", kunden_zuweisung_geaendert: "Kunden-Zuweisung geändert",
  einladung_gesendet: "Einladung gesendet", benutzer_angelegt: "Benutzer angelegt",
  passwort_zurueckgesetzt: "Passwort zurückgesetzt", "2fa_aktiviert": "2FA aktiviert", "2fa_zurueckgesetzt": "2FA zurückgesetzt",
  "2fa_wiederhergestellt": "2FA zurückgesetzt", "2fa_deaktiviert": "2FA deaktiviert", login_2fa_offen: "2FA-Code erforderlich",
  login_2fa_fehlgeschlagen: "Login fehlgeschlagen", "2fa_gesperrt": "Login gesperrt", "2fa_setup_fehlgeschlagen": "Login fehlgeschlagen",
  "2fa_deaktivierung_fehlgeschlagen": "Login fehlgeschlagen", "2fa_wiederherstellung_fehlgeschlagen": "Login fehlgeschlagen",
};
export const POLICY_DOT = { green: "bg-[#A3E635]", amber: "bg-amber-400", red: "bg-red-500", gray: "bg-gray-600", blue: "bg-sky-400" };
export const POLICY_TEXT = { green: "text-[#A3E635]", amber: "text-amber-300", red: "text-red-300", gray: "text-gray-400", blue: "text-sky-300" };

export const TABS = [
  { key: "overview", label: "Übersicht", icon: LayoutGrid },
  { key: "team", label: "KEWAG-Team", icon: Users2 },
  { key: "clients", label: "Kundenbenutzer", icon: Building2 },
  { key: "roles", label: "Rollen & Rechte", icon: SlidersHorizontal },
  { key: "assign", label: "Kunden-Zuweisungen", icon: Target },
  { key: "audit", label: "Audit-Log", icon: ScrollText },
];

export const deDT = (v) => (v ? new Date(v).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }) : null);
export const teamRoleLabel = (matrix, key) => matrix?.roles?.find((r) => r.key === key)?.label || key || "—";

const ST_ACCENT = { lime: "text-[#A3E635]", amber: "text-amber-400", red: "text-red-400", blue: "text-sky-300", gray: "text-gray-300", default: "text-white" };
export function StatTile({ icon: Icon, label, value, sub, accent = "default", testid }) {
  return (
    <div className="bg-[#121217] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400 mb-3">{Icon && <Icon className="w-4 h-4 shrink-0" />} <span className="truncate">{label}</span></div>
      <div className={`text-3xl font-black ${ST_ACCENT[accent]}`}>{value}</div>
      {sub ? <div className="text-xs text-gray-500 mt-0.5 truncate">{sub}</div> : null}
    </div>
  );
}
export function MenuItem({ icon: Icon, label, onClick, testid, tone }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-white/5 transition-colors ${tone === "red" ? "text-red-300" : "text-gray-200"}`}>
      <Icon className="w-4 h-4 shrink-0" /> {label}
    </button>
  );
}
export function QuickBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 text-sm font-medium py-2.5 px-3 rounded-lg flex items-center gap-2.5 transition-colors">
      <Icon className="w-4 h-4 text-[#A3E635]" /> {label}
    </button>
  );
}
export function Modal({ title, onClose, children, testid, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${wide ? "max-w-lg" : "max-w-md"} bg-[#16161c] border border-white/10 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#16161c] z-10">
          <h3 className="font-bold">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
export function Field({ label, children }) {
  return <div className="mb-3"><label className="text-xs text-gray-400 mb-1 block">{label}</label>{children}</div>;
}
export const INP_STYLE = `.inp{width:100%;background:#000;border:1px solid rgba(255,255,255,.1);border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;color:#fff;outline:none}.inp:focus{border-color:#A3E635}`;

export const CRITICAL_EVENTS = new Set([
  "rolle_geaendert", "team_rolle_geaendert", "kunden_zuweisung_geaendert",
  "konto_gesperrt", "2fa_zurueckgesetzt", "2fa_wiederhergestellt",
  "passwort_zurueckgesetzt", "benutzer_angelegt",
]);
export const AUDIT_FILTERS = [
  { key: "all", label: "Alle" },
  { key: "logins", label: "Logins" },
  { key: "2fa", label: "2FA" },
  { key: "roles", label: "Rollenänderungen" },
  { key: "locks", label: "Sperrungen" },
  { key: "password", label: "Passwort" },
  { key: "critical", label: "Kritische Aktionen" },
];
export function eventCategory(ev) {
  const e = ev || "";
  const cats = [];
  if (e.startsWith("login") && e !== "login_2fa_offen") cats.push("logins");
  if (e === "login_2fa_offen" || e.includes("2fa")) cats.push("2fa");
  if (["rolle_geaendert", "team_rolle_geaendert", "kunden_zuweisung_geaendert"].includes(e)) cats.push("roles");
  if (["konto_gesperrt", "konto_entsperrt", "sperre_aufgehoben", "login_gesperrt", "2fa_gesperrt"].includes(e)) cats.push("locks");
  if (e === "passwort_zurueckgesetzt") cats.push("password");
  if (CRITICAL_EVENTS.has(e)) cats.push("critical");
  return cats;
}

const MINI_TONE = { lime: "text-[#A3E635]", amber: "text-amber-400", red: "text-red-400", gray: "text-gray-500", blue: "text-sky-300", default: "text-white" };
export function MiniStat({ label, value, tone = "default", testid }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={testid}>
      <span className="text-xs text-gray-400">{label}</span>
      <span className={`text-sm font-bold ${MINI_TONE[tone] || MINI_TONE.default}`}>{value}</span>
    </div>
  );
}
export function CardBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-100 text-sm font-semibold py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors">
      {Icon && <Icon className="w-4 h-4 text-[#A3E635]" />} {label}
    </button>
  );
}

// Zugriffsstufen fuer die Rollen-Matrix (ehrliche Farben)
export const ACCESS = {
  full: { label: "Vollzugriff", cls: "text-[#A3E635]", dot: "bg-[#A3E635]" },
  read: { label: "Nur lesen", cls: "text-sky-300", dot: "bg-sky-400" },
  limited: { label: "Eingeschränkt", cls: "text-amber-300", dot: "bg-amber-400" },
  none: { label: "Kein Zugriff", cls: "text-gray-600", dot: "bg-gray-700" },
  forbidden: { label: "Nur Super Admin", cls: "text-red-400", dot: "bg-red-500" },
};
export const RISK = {
  kritisch: { label: "Kritisch", cls: "bg-red-500/12 border-red-500/30 text-red-300" },
  hoch: { label: "Hoch", cls: "bg-amber-500/12 border-amber-500/30 text-amber-300" },
  mittel: { label: "Mittel", cls: "bg-sky-500/12 border-sky-500/30 text-sky-300" },
  niedrig: { label: "Niedrig", cls: "bg-emerald-500/12 border-emerald-500/30 text-emerald-300" },
  minimal: { label: "Minimal", cls: "bg-white/5 border-white/15 text-gray-400" },
};

// Styled Select
export function Sel({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:border-[#A3E635] outline-none">
      {options.map((o) => <option key={o.value} value={o.value} className="bg-[#16161c]">{o.label}</option>)}
    </select>
  );
}

// CSV-Export (echte Datei, clientseitig)
export function downloadCsv(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map((h) => esc(h.label)).join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => esc(typeof h.value === "function" ? h.value(r) : r[h.value])).join(",")));
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
export function ExportBtn({ onClick, label, disabled, title, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} data-testid={testid}
      className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
      <Download className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

// Pagination
export function Pagination({ page, setPage, total, perPage, setPerPage, t, testid }) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(total, page * perPage);
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/10 flex-wrap" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span>{t("Pro Seite")}:</span>
        <Sel value={String(perPage)} onChange={(v) => { setPerPage(Number(v)); setPage(1); }} testid={`${testid}-perpage`}
          options={[{ value: "10", label: "10" }, { value: "25", label: "25" }, { value: "50", label: "50" }, { value: "100", label: "100" }]} />
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span data-testid={`${testid}-range`}>{from}-{to} {t("von")} {total}</span>
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} data-testid={`${testid}-prev`} className="p-1.5 rounded-lg bg-white/5 border border-white/10 disabled:opacity-40 hover:bg-white/10 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <span className="px-1">{page} / {pages}</span>
        <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages} data-testid={`${testid}-next`} className="p-1.5 rounded-lg bg-white/5 border border-white/10 disabled:opacity-40 hover:bg-white/10 transition-colors"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// Rechte Seitenleiste / Drawer
export function Drawer({ title, onClose, children, testid }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50" data-testid={testid}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#16161c] border-l border-white/10 shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#16161c] z-10">
          <h3 className="font-bold text-sm">{title}</h3>
          <button onClick={onClose} data-testid={`${testid}-close`} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
export function DrawerRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-0">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className={`text-xs text-right text-gray-200 break-all ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

export function ActionMenu({ u, kind, isOwner, t, busy, menu, setMenu, actions }) {
  return (
    <div className="flex items-center justify-end gap-1 relative">
      {busy === u.id ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : (
        <button onClick={() => setMenu(menu === u.id ? null : u.id)} data-testid={`user-actions-${u.id}`} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"><MoreHorizontal className="w-4 h-4" /></button>
      )}
      {menu === u.id && (
        <>
          <button className="fixed inset-0 z-30 cursor-default" onClick={() => setMenu(null)} aria-label="close" />
          <div className="absolute right-0 top-9 z-40 w-60 bg-[#16161c] border border-white/10 rounded-xl shadow-2xl py-1.5 text-sm" data-testid={`user-menu-${u.id}`}>
            <MenuItem icon={Eye} label={t("Ansehen / Bearbeiten")} onClick={() => actions.edit(u)} testid={`action-edit-${u.id}`} />
            {kind === "team" && !isOwner && <MenuItem icon={UserCog} label={t("Team-Rolle ändern")} onClick={() => actions.teamRole(u)} testid={`action-teamrole-${u.id}`} />}
            {kind === "team" && !isOwner && <MenuItem icon={Target} label={t("Kunden zuweisen")} onClick={() => actions.scope(u)} testid={`action-scope-${u.id}`} />}
            <MenuItem icon={KeyRound} label={t("Passwort zurücksetzen")} onClick={() => actions.resetPw(u)} testid={`action-reset-pw-${u.id}`} />
            <MenuItem icon={RotateCcw} label={t("2FA zurücksetzen")} onClick={() => actions.reset2fa(u)} testid={`action-reset-2fa-${u.id}`} />
            {!isOwner && (u.active === false || u.locked
              ? <MenuItem icon={Unlock} label={t("Konto entsperren")} onClick={() => actions.unlock(u)} testid={`action-unlock-${u.id}`} />
              : <MenuItem icon={Lock} label={t("Konto sperren")} onClick={() => actions.lock(u)} testid={`action-lock-${u.id}`} tone="red" />)}
            <MenuItem icon={Mail} label={t("Einladung erneut senden")} onClick={() => actions.resend(u)} testid={`action-resend-${u.id}`} />
            {!isOwner && <MenuItem icon={ShieldQuestion} label={t("Kontotyp ändern")} onClick={() => actions.role(u)} testid={`action-role-${u.id}`} />}
            {isOwner
              ? <div className="px-3 py-2 text-[11px] text-[#A3E635] flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" /> {t("Owner — geschützt")}</div>
              : <><div className="h-px bg-white/10 my-1" /><MenuItem icon={Trash2} label={t("Löschen")} onClick={() => actions.delete(u)} testid={`action-delete-${u.id}`} tone="red" /></>}
          </div>
        </>
      )}
    </div>
  );
}
